import { useCallback, useMemo, useState } from 'react';
import { useAccount } from '../../lib/account/useAccount';
import { ActivityWizardForm } from './ActivityWizardForm';
import { ActivityComposerContext, type ActivityComposerOptions } from './ActivityComposerContext';

/**
 * Owns a single ActivityWizardForm modal instance for the whole layout.
 * Mount once inside _layout.tsx so LogActivityButton can open it from
 * anywhere in the tree without duplicating modal instances.
 */
export function ActivityComposerProvider({ children }: { children: React.ReactNode }) {
  // null while closed; the options object (possibly empty) while open.
  const [openWith, setOpenWith] = useState<Partial<ActivityComposerOptions> | null>(null);
  const { account } = useAccount();

  const openComposer = useCallback((options?: ActivityComposerOptions) => setOpenWith(options ?? {}), []);
  const closeComposer = useCallback(() => setOpenWith(null), []);

  const value = useMemo(() => ({ openComposer }), [openComposer]);

  return (
    <ActivityComposerContext value={value}>
      {children}
      {openWith && (
        <ActivityWizardForm
          clientProjectId={openWith.clientProjectId}
          initialClientId={openWith.clientId}
          onSuccess={closeComposer}
          onCancel={closeComposer}
          approveOnSave
          timeIncrement={account?.practiceSettings?.timeIncrement ?? null}
        />
      )}
    </ActivityComposerContext>
  );
}
